import { lazy, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ReactComponent as FaUsers } from '../assets/images/svg/users-double.svg';
import { ReactComponent as Alarm } from '../assets/images/svg/clock-hour-4.svg';
import { ReactComponent as MembersIcon } from '../assets/images/svg/members-icon.svg';
import { ReactComponent as EventsIcon } from '../assets/images/svg/link-event.svg';
import { ReactComponent as AppointmentIcon } from '../assets/images/svg/calendar-event.svg';
import { ReactComponent as UserSquare } from '../assets/images/svg/user-edit.svg';
import { ReactComponent as IntegrationIcon } from '../assets/images/svg/puzzle.svg';
import { ReactComponent as SubscriptionIcon } from '../assets/images/svg/crown-alt.svg';
import { ReactComponent as IdentityIcon } from '../assets/images/svg/identity.svg';
import { ReactComponent as LogOutIcon } from '../assets/images/svg/logOut.svg';
import { ReactComponent as Logo_7 } from '../assets/images/svg/logo_7.svg';
import { ReactComponent as SyncIcon } from '../assets/images/svg/calendar-edit.svg';
import { ReactComponent as WorkflowIcon } from '../assets/images/svg/shuffle.svg';
import { ReactComponent as RoutesIcons } from '../assets/images/svg/s-turn-right.svg';
import { ReactComponent as DomainIcon } from '../assets/images/svg/link.svg';
import { ReactComponent as PaymentsIcon } from '../assets/images/svg/money-bills.svg';
import { GrGroup } from 'react-icons/gr';

import Integrations from '../views/settings/integrations';
import { FiBarChart } from 'react-icons/fi';
import { IoIosSettings } from 'react-icons/io';
import Workflow from '../views/workflow';
import UpdateWorkflow from '../views/workflow/updateWorkflow';

const Appointments = lazy(() => import('../views/appointments'));
const DetailsAppointments = lazy(
  () => import('../views/appointments/detailsAppointments')
);
const AuthSections = lazy(() => import('../views/auth'));
const LoginOptions = lazy(() => import('../views/auth/loginOptions'));
const ConfirmEmail = lazy(() => import('../views/auth/confirmEmail'));
const BlockingPeriod = lazy(() => import('../views/blockingPeriod'));
const AddNewAppointment = lazy(
  () => import('../views/appointments/addAppointment')
);
const Clients = lazy(() => import('../views/clients'));
const Members = lazy(() => import('../views/settings/members'));
const Team = lazy(() => import('../views/teams'));
const Group = lazy(() => import('../views/members/groups'));
const ClientDetail = lazy(
  () => import('../views/clients/components/clientDetails')
);
const EditTeam = lazy(() => import('../views/members/components/editTeam'));
const AddNewMember = lazy(
  () => import('../views/settings/members/components/addMember')
);
const EditMember = lazy(
  () => import('../views/settings/members/components/editMember')
);
const AddNewTeam = lazy(() => import('../views/teams/addTeam'));
const ReservationTimes = lazy(() => import('../views/reservationTimes'));
const BlockTimesTable = lazy(
  () => import('../views/reservationTimes/blockTimesTable')
);
const AddBookingTable = lazy(
  () => import('../views/reservationTimes/components/addbookingTable')
);
const AddBlockTime = lazy(
  () => import('../views/reservationTimes/components/addBlockTime')
);
const Reservation = lazy(() => import('../views/reservation'));
const Identity = lazy(() => import('../views/settings/identity'));
const Cancellation = lazy(() => import('../views/reservation/cancellation'));
const ProfileData = lazy(() => import('../views/settings/profile'));
const ManagementProfile = lazy(
  () => import('../views/profile/components/management')
);
const CalenderSync = lazy(() => import('../views/settings/calenderSync'));
const Events = lazy(() => import('../views/events'));
const AddEvent = lazy(() => import('../views/events/components/addEvent'));
const EventOneToOne = lazy(
  () => import('../views/events/components/eventOneToOne')
);
const EventTour = lazy(() => import('../views/events/components/eventTour'));
const GoogleAnalytics = lazy(
  () => import('../views/integration/pages/GoogleAnalytics')
);
const GoogleCalendar = lazy(
  () => import('../views/integration/pages/GoogleCalendar')
);
const Subscription = lazy(() => import('../views/settings/subscription'));
const SubscriptionSuspended = lazy(
  () => import('../views/subscription/SubscriptionSuspended')
);
const DeactivateClients = lazy(
  () => import('../views/subscription/DeactivateClients')
);
const Plans = lazy(() => import('../views/subscription/Plans'));
const PlanSummary = lazy(() => import('../views/subscription/PlanSummary'));
const CustomDomain = lazy(() => import('../views/settings/customDomain'));
const LogOut = lazy(() => import('../views/logOut'));
const SignIn = lazy(() => import('../views/auth/signIn'));
const ConfirmMobile = lazy(() => import('../views/auth/confirmMobile'));
const ChooseAccount = lazy(() => import('../views/auth/chooseAccount'));

const Registration = lazy(() => import('../views/auth/registeration'));
const Invitation = lazy(() => import('../views/auth/invitation'));
const Settings = lazy(() => import('../views/settings'));
const OrientationModels = lazy(() => import('../views/orientationModels'));
const UpdateRoute = lazy(
  () => import('../views/orientationModels/updateRouting')
);
const Replies = lazy(() => import('../views/orientationModels/replies'));
const GoogleLoginRedirect = lazy(
  () => import('../views/auth/redirectGoogleLogin')
);
const Analytics = lazy(() => import('../views/analytics'));

export interface IRoute<T = any> {
  component: JSX.Element;
  path: string;
  icon?: JSX.Element;
  privileges: true;
  to: (props?: T) => string;
  fullTitle: (props?: T) => { name: string; link?: string }[];
  title: () => string;
  sidebarTitle?: () => string;
  subLinks?: string[];
  sideBarActiveState?: string;
}

export interface IRoutes {
  authSections: IRoute;
  registerSections: IRoute;
  loginOptions: IRoute;
  registerOptions: IRoute;
  signIn: IRoute;
  register: IRoute;
  registerEmail: IRoute;
  otpVerify: IRoute;
  chooseAccount: IRoute;
  newAccount: IRoute;
  verifyEmail: IRoute;
  verifyEmailAfterRegister: IRoute;
  invitation: IRoute;
  appointments: IRoute;
  detailsAppointments: IRoute<{ id: string }>;
  addAppointment: IRoute;
  customDomain: IRoute;
  settingsPayments: IRoute;
  events: IRoute;
  addEvents: IRoute;
  editEventOneToOne: IRoute<{ id: string; slug: string; team_id?: string }>;
  editEventOneToGroup: IRoute<{ id: string; slug: string; team_id?: string }>;
  editEventTour: IRoute<{ id: string; slug: string; team_id?: string }>;
  eventOneToOne: IRoute;
  eventOneToGroup: IRoute;
  collectiveEvent: IRoute;
  eventTour: IRoute;
  reservationTimes: IRoute;
  blockTimes: IRoute;
  bookingTables: IRoute;
  addBookingTable: IRoute;
  editBookingTable: IRoute;
  addBlockTime: IRoute;
  editBlockTime: IRoute;
  clients: IRoute;
  clientDetail: IRoute;
  members: IRoute;
  reservation: IRoute;
  profileData: IRoute;
  managementProfile: IRoute;
  calenderSync: IRoute;
  identity: IRoute;
  cancellationPolicy: IRoute;
  member: IRoute;
  addMember: IRoute;
  editMember: IRoute<{ id: string }>;
  memberGroups: IRoute;
  team: IRoute;
  addTeam: IRoute;
  editTeam: IRoute<{ id: string }>;
  settings: IRoute;
  blockingPeriod: IRoute;
  integration: IRoute;
  googleAnalytics: IRoute<{ id: string }>;
  googleCalendar: IRoute<{ id: string }>;
  subscription: IRoute;
  subscriptionSuspended: IRoute;
  deactivateClients: IRoute;
  plans: IRoute;
  planSummary: IRoute;
  // customDomain: IRoute;
  logOut: IRoute;
  models: IRoute;
  updateModel: IRoute;
  replies: IRoute;
  googleLoginRedirect: IRoute;
  workflows: IRoute;
  updateWorkflow: IRoute;
  analytics: IRoute;
  editEventCollective: IRoute;
  editEventMeetingPolls: IRoute;
  eventPolls: IRoute;
}

export function useRoutesConst() {
  const { formatMessage } = useIntl();

  const ROUTES: IRoutes = useMemo(() => {
    const routes: IRoutes = {
      authSections: {
        component: <AuthSections />,
        path: 'login',
        privileges: true,
        to: () => '/login',
        fullTitle: () => [{ name: formatMessage({ id: 'login' }) }],
        title: () => formatMessage({ id: 'login' }),
        sidebarTitle: () => formatMessage({ id: 'login' }),
      },
      registerSections: {
        component: <AuthSections />,
        path: 'register',
        privileges: true,
        to: () => '/register',
        fullTitle: () => [{ name: formatMessage({ id: 'login' }) }],
        title: () => formatMessage({ id: 'login' }),
        sidebarTitle: () => formatMessage({ id: 'login' }),
      },
      loginOptions: {
        component: <LoginOptions isRegister={false} />,
        path: 'login',
        privileges: true,
        to: () => '/login',
        fullTitle: () => [{ name: formatMessage({ id: 'login' }) }],
        title: () => formatMessage({ id: 'login' }),
        sidebarTitle: () => formatMessage({ id: 'login' }),
      },
      registerOptions: {
        component: <LoginOptions isRegister={true} />,
        path: 'register',
        privileges: true,
        to: () => '/register',
        fullTitle: () => [{ name: formatMessage({ id: 'login' }) }],
        title: () => formatMessage({ id: 'login' }),
        sidebarTitle: () => formatMessage({ id: 'login' }),
      },
      signIn: {
        component: <SignIn isRegister={false} />,
        path: 'email',
        privileges: true,
        to: () => '/login/email',
        fullTitle: () => [{ name: formatMessage({ id: 'login' }) }],
        title: () => formatMessage({ id: 'login' }),
        sidebarTitle: () => formatMessage({ id: 'login' }),
      },
      googleLoginRedirect: {
        component: <GoogleLoginRedirect />,
        path: 'google-login-redirect',
        privileges: true,
        to: () => '/google-login-redirect',
        fullTitle: () => [{ name: formatMessage({ id: 'login' }) }],
        title: () => formatMessage({ id: 'login' }),
        sidebarTitle: () => formatMessage({ id: 'login' }),
      },
      registerEmail: {
        component: <SignIn isRegister={true} />,
        path: 'email',
        privileges: true,
        to: () => '/register/email',
        fullTitle: () => [{ name: formatMessage({ id: 'login' }) }],
        title: () => formatMessage({ id: 'login' }),
        sidebarTitle: () => formatMessage({ id: 'login' }),
      },
      register: {
        component: <Registration />,
        path: 'new-user',
        privileges: true,
        to: () => '/register/new-user',
        fullTitle: () => [{ name: formatMessage({ id: 'login' }) }],
        title: () => formatMessage({ id: 'login' }),
        sidebarTitle: () => formatMessage({ id: 'login' }),
      },
      otpVerify: {
        component: <ConfirmMobile />,
        path: 'verify-phone',
        privileges: true,
        to: () => '/verify-phone',
        fullTitle: () => [{ name: formatMessage({ id: 'login' }) }],
        title: () => formatMessage({ id: 'login' }),
        sidebarTitle: () => formatMessage({ id: 'login' }),
      },
      chooseAccount: {
        component: <ChooseAccount />,
        path: 'choose-account',
        privileges: true,
        to: () => '/login/choose-account',
        fullTitle: () => [{ name: formatMessage({ id: 'login' }) }],
        title: () => formatMessage({ id: 'login' }),
        sidebarTitle: () => formatMessage({ id: 'login' }),
      },
      newAccount: {
        component: <Registration />,
        path: 'new-account',
        privileges: true,
        to: () => '/login/new-account',
        fullTitle: () => [{ name: formatMessage({ id: 'login' }) }],
        title: () => formatMessage({ id: 'login' }),
        sidebarTitle: () => formatMessage({ id: 'login' }),
      },
      verifyEmail: {
        component: <ConfirmEmail isRegister={false} />,
        path: 'verify-email',
        privileges: true,
        to: (props) => `/login/verify-email`,
        fullTitle: () => [{ name: formatMessage({ id: 'login' }) }],
        title: () => formatMessage({ id: 'login' }),
        sidebarTitle: () => formatMessage({ id: 'login' }),
      },
      verifyEmailAfterRegister: {
        component: <ConfirmEmail isRegister={true} />,
        path: 'verify-email',
        privileges: true,
        to: () => `/register/verify-email`,
        fullTitle: () => [{ name: formatMessage({ id: 'login' }) }],
        title: () => formatMessage({ id: 'login' }),
        sidebarTitle: () => formatMessage({ id: 'login' }),
      },

      // addWorkHours: {
      //   component: <AccountSchedule />,
      //   path: 'account-schedule',
      //   privileges: true,
      //   to: () => '/login/account-schedule',
      //   fullTitle: () => [{ name: formatMessage({ id: 'login' }) }],
      //   title: () => formatMessage({ id: 'login' }),
      //   sidebarTitle: () => formatMessage({ id: 'login' }),
      // },
      // newTeam: {
      //   component: <NewTeam />,
      //   path: 'new-team',
      //   privileges: true,
      //   to: () => '/login/new-team',
      //   fullTitle: () => [{ name: formatMessage({ id: 'login' }) }],
      //   title: () => formatMessage({ id: 'login' }),
      //   sidebarTitle: () => formatMessage({ id: 'login' }),
      // },
      invitation: {
        component: <Invitation />,
        path: 'i/:id',
        privileges: true,
        to: (props) => `i/${props?.id}`,
        fullTitle: () => [{ name: formatMessage({ id: 'login' }) }],
        title: () => formatMessage({ id: 'login' }),
        sidebarTitle: () => formatMessage({ id: 'login' }),
      },
      // ---------------------------------------------------------------- //
      appointments: {
        component: <Appointments />,
        path: 'appointments',
        icon: <AppointmentIcon />,
        privileges: true,
        to: () => '/appointments',
        fullTitle: () => [{ name: formatMessage({ id: 'meetings' }) }],
        title: () => formatMessage({ id: 'meetings' }),
        sidebarTitle: () => formatMessage({ id: 'meetings' }),
        subLinks: [
          'appointments',
          'blocking-period',
          '/appointments/add',
          'details-upcoming-appointments',
        ],
        sideBarActiveState: 'appointments',
      },
      detailsAppointments: {
        component: <DetailsAppointments />,
        path: ':id',
        privileges: true,
        to: (props) => `/appointments/${props?.id}`,
        fullTitle: (props) => [
          {
            name: formatMessage({ id: 'detailsUpcomingAppointments' }),
          },
        ],
        title: () => formatMessage({ id: 'detailsUpcomingAppointments' }),
        sidebarTitle: () =>
          formatMessage({ id: 'detailsUpcomingAppointments' }),
        sideBarActiveState: 'appointments',
      },
      addAppointment: {
        component: <AddNewAppointment />,
        path: 'add',
        icon: <Logo_7 />,
        privileges: true,
        to: () => '/appointments/add',
        fullTitle: () => [{ name: formatMessage({ id: 'addNewAppointment' }) }],
        title: () => formatMessage({ id: 'addNewAppointment' }),
        sidebarTitle: () => formatMessage({ id: 'addNewAppointment' }),
        sideBarActiveState: 'appointments',
      },

      // ---------------------------------------------------------------- //
      events: {
        component: <Events />,
        path: 'events/:team_id?',
        icon: <EventsIcon />,
        privileges: true,
        to: (props) => `/events${props?.team_id ? `/${props.team_id}` : ''}`,
        fullTitle: () => [{ name: formatMessage({ id: 'events' }) }],
        title: () => formatMessage({ id: 'events' }),
        sidebarTitle: () => formatMessage({ id: 'events' }),
        subLinks: ['add-event', 'one-to-one', 'one-to-group', 'tour'],
        sideBarActiveState: 'events',
      },
      addEvents: {
        component: <AddEvent />,
        path: 'add-event/:team_id?',
        privileges: true,
        to: (props) =>
          `/events/add-event${props?.team_id ? `/${props.team_id}` : ''}`,
        fullTitle: () => [{ name: formatMessage({ id: 'events' }) }],
        title: () => formatMessage({ id: 'events' }),
        sidebarTitle: () => formatMessage({ id: 'events' }),
        sideBarActiveState: 'events',
      },
      editEventOneToOne: {
        component: <EventOneToOne />,
        path: 'edit/one_to_one/:id/:team_id?',
        privileges: true,
        to: (props: any) =>
          `/events/edit/${props?.slug}/${props?.id}${
            props?.team_id ? `/${props.team_id}` : ''
          }`,
        fullTitle: () => [{ name: formatMessage({ id: 'events' }) }],
        title: () => formatMessage({ id: 'events' }),
        sidebarTitle: () => formatMessage({ id: 'events' }),
        sideBarActiveState: 'events',
      },
      editEventOneToGroup: {
        component: <EventOneToOne type="group" />,
        path: 'edit/one_to_many/:id/:team_id?',
        privileges: true,
        to: (props: any) =>
          `/events/edit/${props?.slug}/${props?.id}${
            props?.team_id ? `/${props.team_id}` : ''
          }`,
        fullTitle: () => [{ name: formatMessage({ id: 'events' }) }],
        title: () => formatMessage({ id: 'events' }),
        sidebarTitle: () => formatMessage({ id: 'events' }),
        sideBarActiveState: 'events',
      },
      editEventTour: {
        component: <EventOneToOne type="tour" />,
        path: 'edit/round_robin/:id/:team_id?',
        privileges: true,
        to: (props: any) =>
          `/events/edit/${props?.slug}/${props?.id}${
            props?.team_id ? `/${props.team_id}` : ''
          }`,
        fullTitle: () => [{ name: formatMessage({ id: 'events' }) }],
        title: () => formatMessage({ id: 'events' }),
        sidebarTitle: () => formatMessage({ id: 'events' }),
        sideBarActiveState: 'events',
      },
      editEventCollective: {
        component: <EventOneToOne type="collective" />,
        path: 'edit/collective_event/:id/:team_id?',
        privileges: true,
        to: (props: any) =>
          `/events/edit/${props?.slug}/${props?.id}${
            props?.team_id ? `/${props.team_id}` : ''
          }`,
        fullTitle: () => [{ name: formatMessage({ id: 'events' }) }],
        title: () => formatMessage({ id: 'events' }),
        sidebarTitle: () => formatMessage({ id: 'events' }),
        sideBarActiveState: 'events',
      },
      editEventMeetingPolls: {
        component: <EventOneToOne type="meeting_polls" />,
        path: 'edit/meeting_polls/:id/:team_id?',
        privileges: true,
        to: (props: any) =>
          `/events/edit/${props?.slug}/${props?.id}${
            props?.team_id ? `/${props.team_id}` : ''
          }`,
        fullTitle: () => [{ name: formatMessage({ id: 'events' }) }],
        title: () => formatMessage({ id: 'events' }),
        sidebarTitle: () => formatMessage({ id: 'events' }),
        sideBarActiveState: 'events',
      },
      eventOneToOne: {
        component: <EventOneToOne />,
        path: 'one-to-one/:team_id?',
        privileges: true,
        to: (props: any) =>
          `/events/add-event/one-to-one${
            props?.team_id ? `/${props.team_id}` : ''
          }`,
        fullTitle: () => [{ name: formatMessage({ id: 'events' }) }],
        title: () => formatMessage({ id: 'events' }),
        sidebarTitle: () => formatMessage({ id: 'events' }),
        sideBarActiveState: 'events',
      },
      eventPolls: {
        component: <EventOneToOne type="meeting_polls" />,
        path: 'meeting_polls/:team_id?',
        privileges: true,
        to: (props: any) =>
          `/events/add-event/meeting_polls${
            props?.team_id ? `/${props.team_id}` : ''
          }`,
        fullTitle: () => [{ name: formatMessage({ id: 'events' }) }],
        title: () => formatMessage({ id: 'events' }),
        sidebarTitle: () => formatMessage({ id: 'events' }),
        sideBarActiveState: 'events',
      },
      eventOneToGroup: {
        component: <EventOneToOne type="group" />,
        path: 'one-to-group/:team_id?',
        privileges: true,
        to: (props: any) =>
          `/events/add-event/one-to-group${
            props?.team_id ? `/${props.team_id}` : ''
          }`,
        fullTitle: () => [{ name: formatMessage({ id: 'events' }) }],
        title: () => formatMessage({ id: 'events' }),
        sidebarTitle: () => formatMessage({ id: 'events' }),
        sideBarActiveState: 'events',
      },
      collectiveEvent: {
        component: <EventOneToOne type="collective" />,
        path: 'collective/:team_id?',
        privileges: true,
        to: (props: any) =>
          `/events/add-event/collective${
            props?.team_id ? `/${props.team_id}` : ''
          }`,
        fullTitle: () => [{ name: formatMessage({ id: 'events' }) }],
        title: () => formatMessage({ id: 'events' }),
        sidebarTitle: () => formatMessage({ id: 'events' }),
        sideBarActiveState: 'events',
      },
      eventTour: {
        component: <EventOneToOne type="tour" />,
        path: 'tour/:team_id?',
        privileges: true,
        to: (props: any) =>
          `/events/add-event/tour${props?.team_id ? `/${props.team_id}` : ''}`,
        fullTitle: () => [{ name: formatMessage({ id: 'events' }) }],
        title: () => formatMessage({ id: 'events' }),
        sidebarTitle: () => formatMessage({ id: 'events' }),
        sideBarActiveState: 'events',
      },

      // ---------------------------------------------------------------- //
      reservationTimes: {
        component: <ReservationTimes />,
        path: 'reservation-times',
        icon: <Alarm />,
        privileges: true,
        to: () => '/reservation-times',
        fullTitle: () => [{ name: formatMessage({ id: 'bookingTimes' }) }],
        title: () => formatMessage({ id: 'bookingTimes' }),
        sidebarTitle: () => formatMessage({ id: 'bookingTimes' }),
        subLinks: [
          'booking-tables',
          'block-times',
          'add-table',
          'add-block-time',
        ],
        sideBarActiveState: 'reservation-times',
      },
      blockTimes: {
        component: <BlockTimesTable />,
        path: 'block-times',
        icon: <Alarm />,
        privileges: true,
        to: () => '/reservation-times/block-times',
        fullTitle: () => [{ name: formatMessage({ id: 'bookingTimes' }) }],
        title: () => formatMessage({ id: 'bookingTimes' }),
        sidebarTitle: () => formatMessage({ id: 'bookingTimes' }),
        sideBarActiveState: 'reservation-times',
      },
      addBlockTime: {
        component: <AddBlockTime />,
        path: 'add',
        privileges: true,
        to: () => `/reservation-times/block-times/add`,
        fullTitle: () => [{ name: formatMessage({ id: 'bookingTimes' }) }],
        title: () => formatMessage({ id: 'bookingTimes' }),
        sidebarTitle: () => formatMessage({ id: 'bookingTimes' }),
        sideBarActiveState: 'reservation-times',
      },
      editBlockTime: {
        component: <AddBlockTime />,
        path: 'edit/:id',
        privileges: true,
        to: (props) => `/reservation-times/block-times/edit/${props?.id}`,
        fullTitle: () => [{ name: formatMessage({ id: 'bookingTimes' }) }],
        title: () => formatMessage({ id: 'bookingTimes' }),
        sidebarTitle: () => formatMessage({ id: 'bookingTimes' }),
        sideBarActiveState: 'reservation-times',
      },
      bookingTables: {
        component: <ReservationTimes />,
        path: 'booking-tables',
        icon: <Alarm />,
        privileges: true,
        to: () => '/reservation-times/booking-tables',
        fullTitle: () => [{ name: formatMessage({ id: 'bookingTimes' }) }],
        title: () => formatMessage({ id: 'bookingTimes' }),
        sidebarTitle: () => formatMessage({ id: 'bookingTimes' }),
        sideBarActiveState: 'reservation-times',
      },
      addBookingTable: {
        component: <AddBookingTable />,
        path: 'add',
        privileges: true,
        to: () => `/reservation-times/booking-tables/add`,
        fullTitle: () => [{ name: formatMessage({ id: 'bookingTimes' }) }],
        title: () => formatMessage({ id: 'bookingTimes' }),
        sidebarTitle: () => formatMessage({ id: 'bookingTimes' }),
        sideBarActiveState: 'reservation-times',
      },
      editBookingTable: {
        component: <AddBookingTable />,
        path: 'edit/:id',
        privileges: true,
        to: (props) => `/reservation-times/booking-tables/edit/${props?.id}`,
        fullTitle: () => [{ name: formatMessage({ id: 'bookingTimes' }) }],
        title: () => formatMessage({ id: 'bookingTimes' }),
        sidebarTitle: () => formatMessage({ id: 'bookingTimes' }),
        sideBarActiveState: 'reservation-times',
      },

      // ---------------------------------------------------------------- //
      clients: {
        component: <Clients />,
        path: 'clients',
        icon: <FaUsers />,
        privileges: true,
        to: () => '/clients',
        fullTitle: () => [{ name: formatMessage({ id: 'contacts' }) }],
        title: () => formatMessage({ id: 'contacts' }),
        sidebarTitle: () => formatMessage({ id: 'contacts' }),
        subLinks: ['add-new-client', 'client-details'],
        sideBarActiveState: 'clients',
      },
      clientDetail: {
        component: <ClientDetail />,
        path: ':id',
        privileges: true,
        to: (props) => `/clients/${props?.id}`,
        fullTitle: (props) => [
          {
            name: formatMessage({ id: 'clientDetail' }),
          },
        ],
        title: () => formatMessage({ id: 'clientDetail' }),
        sidebarTitle: () => formatMessage({ id: 'clientDetail' }),
        sideBarActiveState: 'clients',
      },

      // ---------------------------------------------------------------- //
      members: {
        component: <Members />,
        path: 'members',
        icon: <MembersIcon />,
        privileges: true,
        to: () => '/members',
        fullTitle: () => [{ name: formatMessage({ id: 'members' }) }],
        title: () => formatMessage({ id: 'members' }),
        sidebarTitle: () => formatMessage({ id: 'members' }),
        subLinks: ['member'],
        sideBarActiveState: 'members',
      },
      member: {
        component: <Members />,
        path: 'member',
        privileges: true,
        to: () => `/members/member`,
        fullTitle: () => [{ name: formatMessage({ id: 'members' }) }],
        title: () => formatMessage({ id: 'members' }),
        sidebarTitle: () => formatMessage({ id: 'members' }),
        sideBarActiveState: 'members',
      },
      addMember: {
        component: <AddNewMember />,
        path: 'add-member',
        privileges: true,
        to: () => `/members/member/add-member`,
        fullTitle: () => [{ name: formatMessage({ id: 'members' }) }],
        title: () => formatMessage({ id: 'members' }),
        sidebarTitle: () => formatMessage({ id: 'members' }),
        sideBarActiveState: 'members',
      },
      editMember: {
        component: <EditMember />,
        path: 'edit-member/:id',
        privileges: true,
        to: (props) => `/members/member/edit-member/${props?.id}`,
        fullTitle: () => [{ name: formatMessage({ id: 'members' }) }],
        title: () => formatMessage({ id: 'members' }),
        sidebarTitle: () => formatMessage({ id: 'members' }),
        sideBarActiveState: 'members',
      },
      memberGroups: {
        component: <Group />,
        path: 'groups',
        privileges: true,
        to: () => '/members/groups',
        fullTitle: () => [{ name: formatMessage({ id: 'groups' }) }],
        title: () => formatMessage({ id: 'groups' }),
        sidebarTitle: () => formatMessage({ id: 'groups' }),
        subLinks: [],
        sideBarActiveState: 'members',
      },
      team: {
        component: <Team />,
        path: 'teams',
        icon: <GrGroup />,
        privileges: true,
        to: () => `/teams`,
        fullTitle: () => [{ name: formatMessage({ id: 'teams' }) }],
        title: () => formatMessage({ id: 'teams' }),
        sidebarTitle: () => formatMessage({ id: 'teams' }),
        subLinks: ['add-team', 'edit-team'],
        sideBarActiveState: 'teams',
      },
      addTeam: {
        component: <AddNewTeam />,
        path: 'add-team',
        privileges: true,
        to: () => `/teams/add-team`,
        fullTitle: () => [{ name: formatMessage({ id: 'teams' }) }],
        title: () => formatMessage({ id: 'teams' }),
        sidebarTitle: () => formatMessage({ id: 'teams' }),
        sideBarActiveState: 'teams',
      },
      editTeam: {
        component: <AddNewTeam />,
        path: 'edit-team/:id',
        privileges: true,
        to: (props) => `/teams/edit-team/${props?.id}`,
        fullTitle: () => [{ name: formatMessage({ id: 'teams' }) }],
        title: () => formatMessage({ id: 'teams' }),
        sidebarTitle: () => formatMessage({ id: 'teams' }),
        sideBarActiveState: 'teams',
      },

      // ---------------------------------------------------------------- //
      reservation: {
        component: <Reservation />,
        path: 'reservation',
        icon: <AppointmentIcon />,
        privileges: true,
        to: () => '/reservation',
        fullTitle: () => [{ name: formatMessage({ id: 'bookingPage' }) }],
        title: () => formatMessage({ id: 'bookingPage' }),
        sidebarTitle: () => formatMessage({ id: 'bookingPage' }),
        subLinks: ['identity', 'cancellation-policy'],
        sideBarActiveState: 'reservation',
      },
      cancellationPolicy: {
        component: <Cancellation />,
        path: 'cancellation-policy',
        privileges: true,
        to: () => `/reservation/cancellation-policy`,
        fullTitle: () => [{ name: formatMessage({ id: 'bookingPage' }) }],
        title: () => formatMessage({ id: 'bookingPage' }),
        sidebarTitle: () => formatMessage({ id: 'bookingPage' }),
        sideBarActiveState: 'reservation',
      },
      googleAnalytics: {
        component: <GoogleAnalytics />,
        path: 'google-analytics',
        privileges: true,
        to: () => '/integration/google-analytics',
        fullTitle: () => [{ name: formatMessage({ id: 'Integration' }) }],
        title: () => formatMessage({ id: 'Integration' }),
        sidebarTitle: () => formatMessage({ id: 'Integration' }),
        sideBarActiveState: 'integration',
      },
      googleCalendar: {
        component: <GoogleCalendar />,
        path: 'google-calendar',
        privileges: true,
        to: () => '/integration/google-calendar',
        fullTitle: () => [{ name: formatMessage({ id: 'Integration' }) }],
        title: () => formatMessage({ id: 'Integration' }),
        sidebarTitle: () => formatMessage({ id: 'Integration' }),
        sideBarActiveState: 'integration',
      },

      logOut: {
        component: <LogOut />,
        path: 'logOut',
        icon: <LogOutIcon />,
        privileges: true,
        to: () => '/logOut',
        fullTitle: () => [{ name: formatMessage({ id: 'Logout' }) }],
        title: () => formatMessage({ id: 'Logout' }),
        sidebarTitle: () => formatMessage({ id: 'Logout' }),
        sideBarActiveState: 'logOut',
      },

      // ---------------------------------------------------------------- //

      settings: {
        component: <Settings />,
        path: 'settings/:type?/:id?',
        privileges: true,
        icon: <IoIosSettings color="#141414" size={16} />,
        to: (props) => `/settings/profile`,
        fullTitle: () => [{ name: formatMessage({ id: 'settings' }) }],
        title: () => formatMessage({ id: 'settings' }),
        sidebarTitle: () => formatMessage({ id: 'settings' }),
        sideBarActiveState: 'settings',
      },
      profileData: {
        component: <ProfileData />,
        path: 'profile',
        icon: <UserSquare />,
        privileges: true,
        to: () => '/profile',
        fullTitle: () => [{ name: formatMessage({ id: 'profile' }) }],
        title: () => formatMessage({ id: 'profile' }),
        sidebarTitle: () => formatMessage({ id: 'profile' }),
        sideBarActiveState: 'profile',
      },
      subscription: {
        component: <Subscription />,
        path: 'subscription',
        icon: <SubscriptionIcon />,
        privileges: true,
        to: () => '/subscription',
        fullTitle: () => [{ name: formatMessage({ id: 'Subscription' }) }],
        title: () => formatMessage({ id: 'Subscription' }),
        sidebarTitle: () => formatMessage({ id: 'Subscription' }),
        sideBarActiveState: 'subscription',
      },
      subscriptionSuspended: {
        component: <SubscriptionSuspended />,
        path: 'subscription-suspended',
        privileges: true,
        to: () => '/subscription-suspended',
        fullTitle: () => [
          { name: formatMessage({ id: 'SubscriptionSuspended' }) },
        ],
        title: () => formatMessage({ id: 'SubscriptionSuspended' }),
        sidebarTitle: () => formatMessage({ id: 'SubscriptionSuspended' }),
        sideBarActiveState: 'subscription-suspended',
      },
      deactivateClients: {
        component: <DeactivateClients />,
        path: 'subscriptions/deactivate-clients',
        privileges: true,
        to: () => '/subscriptions/deactivate-clients',
        fullTitle: () => [{ name: formatMessage({ id: 'deactivateMembers' }) }],
        title: () => formatMessage({ id: 'deactivateMembers' }),
        sidebarTitle: () => formatMessage({ id: 'deactivateMembers' }),
      },
      plans: {
        component: <Plans />,
        path: 'plans',
        privileges: true,
        to: () => '/plans',
        fullTitle: () => [{ name: 'باقات الأسعار' }],
        title: () => 'باقات الأسعار',
        sidebarTitle: () => 'باقات الأسعار',
        sideBarActiveState: 'plans',
      },
      planSummary: {
        component: <PlanSummary />,
        path: 'plans/summary',
        privileges: true,
        to: () => '/plans/summary',
        fullTitle: () => [{ name: 'ملخص الاشتراك' }],
        title: () => 'ملخص الاشتراك',
        sidebarTitle: () => 'ملخص الاشتراك',
        sideBarActiveState: 'plans',
      },
      customDomain: {
        component: <CustomDomain />,
        path: 'custom-domain',
        icon: <DomainIcon style={{ width: '14px', height: '14px' }} />,
        privileges: true,
        to: () => '/custom-domain',
        fullTitle: () => [{ name: formatMessage({ id: 'CustomDomain' }) }],
        title: () => formatMessage({ id: 'CustomDomain' }),
        sidebarTitle: () => formatMessage({ id: 'CustomDomain' }),
        sideBarActiveState: 'custom-domain',
      },
      settingsPayments: {
        component: <></>,
        path: 'settings/payments',
        icon: <PaymentsIcon style={{ width: '14px', height: '14px' }} />,
        privileges: true,
        to: () => '/settings/payments',
        fullTitle: () => [{ name: formatMessage({ id: 'payments' }) }],
        title: () => formatMessage({ id: 'payments' }),
        sidebarTitle: () => formatMessage({ id: 'payments' }),
        sideBarActiveState: 'payments',
      },
      integration: {
        component: <Integrations />,
        path: 'integration',
        icon: <IntegrationIcon />,
        privileges: true,
        to: () => '/integration',
        fullTitle: () => [{ name: formatMessage({ id: 'Integration' }) }],
        title: () => formatMessage({ id: 'Integration' }),
        sidebarTitle: () => formatMessage({ id: 'Integration' }),
        subLinks: ['google-analytics'],
        sideBarActiveState: 'integration',
      },
      identity: {
        component: <Identity />,
        path: 'identity',
        privileges: true,
        icon: <IdentityIcon />,
        to: () => `/identity`,
        fullTitle: () => [{ name: formatMessage({ id: 'identity' }) }],
        title: () => formatMessage({ id: 'identity' }),
        sidebarTitle: () => formatMessage({ id: 'identity' }),
      },
      managementProfile: {
        component: <ManagementProfile />,
        path: 'profile/management',
        privileges: true,
        to: () => `/profile/management`,
        fullTitle: () => [{ name: formatMessage({ id: 'profile' }) }],
        title: () => formatMessage({ id: 'profile' }),
        sidebarTitle: () => formatMessage({ id: 'profile' }),
        sideBarActiveState: 'profile',
      },

      calenderSync: {
        component: <CalenderSync />,
        path: 'calendar-sync',
        privileges: true,
        icon: <SyncIcon />,
        to: () => `/calendar-sync`,
        fullTitle: () => [{ name: formatMessage({ id: 'calenderSync' }) }],
        title: () => formatMessage({ id: 'calenderSync' }),
        sidebarTitle: () => formatMessage({ id: 'calenderSync' }),
        sideBarActiveState: 'calenderSync',
      },
      blockingPeriod: {
        component: <BlockingPeriod />,
        path: 'blocking-period',
        icon: <Logo_7 />,
        privileges: true,
        to: () => '/blocking-period',
        fullTitle: () => [{ name: formatMessage({ id: 'BlockingPeriod' }) }],
        title: () => formatMessage({ id: 'BlockingPeriod' }),
        sidebarTitle: () => formatMessage({ id: 'BlockingPeriod' }),
        sideBarActiveState: 'appointments',
      },
      models: {
        component: <OrientationModels />,
        path: 'forms',
        icon: <RoutesIcons />,
        privileges: true,
        to: () => '/forms',
        fullTitle: () => [{ name: formatMessage({ id: 'orientationModels' }) }],
        title: () => formatMessage({ id: 'orientationModels' }),
        sidebarTitle: () => formatMessage({ id: 'orientationModels' }),
        sideBarActiveState: 'models',
      },
      workflows: {
        component: <Workflow />,
        path: 'workflows',
        icon: <WorkflowIcon />,
        privileges: true,
        to: () => '/workflows',
        fullTitle: () => [{ name: formatMessage({ id: 'workflows' }) }],
        title: () => formatMessage({ id: 'workflows' }),
        sidebarTitle: () => formatMessage({ id: 'workflows' }),
        sideBarActiveState: 'workflows',
      },
      updateWorkflow: {
        component: <UpdateWorkflow />,
        path: '/workflows/update/:id',
        icon: <WorkflowIcon />,
        privileges: true,
        to: () => '/workflows/update/:id',
        fullTitle: () => [{ name: formatMessage({ id: 'workflows' }) }],
        title: () => formatMessage({ id: 'workflows' }),
        sidebarTitle: () => formatMessage({ id: 'workflows' }),
        sideBarActiveState: 'workflows',
      },
      updateModel: {
        component: <UpdateRoute />,
        path: '/forms/update/:id',
        icon: <RoutesIcons />,
        privileges: true,
        to: () => '/forms/update/:id',
        fullTitle: () => [{ name: formatMessage({ id: 'orientationModels' }) }],
        title: () => formatMessage({ id: 'orientationModels' }),
        sidebarTitle: () => formatMessage({ id: 'orientationModels' }),
        sideBarActiveState: 'models',
      },
      replies: {
        component: <Replies />,
        path: '/forms/:routeId/replies',
        icon: <RoutesIcons />,
        privileges: true,
        to: () => '/forms/:routeId/replies',
        fullTitle: () => [{ name: formatMessage({ id: 'orientationModels' }) }],
        title: () => formatMessage({ id: 'orientationModels' }),
        sidebarTitle: () => formatMessage({ id: 'orientationModels' }),
        sideBarActiveState: 'models',
      },
      analytics: {
        component: <Analytics />,
        path: 'analytics',
        icon: <FiBarChart color="#141414" size={16} />,
        privileges: true,
        to: () => '/analytics',
        fullTitle: () => [{ name: formatMessage({ id: 'analytics' }) }],
        title: () => formatMessage({ id: 'analytics' }),
        sidebarTitle: () => formatMessage({ id: 'analytics' }),
        sideBarActiveState: 'analytics',
      },
    };
    return routes;
  }, [formatMessage]);

  return { ROUTES };
}
